"use client";

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { FormProvider, useWvcForm } from '@/integrations/forms/FormProvider';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Link } from '@/components/common/Link';
import { Loader, MapPin, Phone, Mail, User, DollarSign, Info } from 'lucide-react';
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
export default function WelcomeLeadCaptureModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
    const hasSeenModal = localStorage.getItem('wvc_welcome_seen');
    if (!hasSeenModal) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      localStorage.setItem('wvc_welcome_seen', 'true');
    }
  };
  if (!isMounted) {
    return <section id="welcomeleadcapturemodal" className="hidden" />;
  }
  return <section id="welcomeleadcapturemodal">
            <Dialog open={isOpen} onOpenChange={handleOpenChange}>
                <DialogContent className="bg-primary text-primary-foreground border-none sm:max-w-2xl overflow-hidden shadow-2xl" showCloseButton={true}>
                    {}
                    <div className="absolute inset-0 opacity-5 pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686519/img/real_estate_watermark.png)] bg-cover bg-center mix-blend-overlay" />

                    <DialogHeader className="relative z-10 text-center flex flex-col items-center gap-4 pt-6 pb-2">
                        <Link to="/" className="mb-6">
                            <WvcLogo className="w-[120px] h-auto" />
                        </Link>
                        <div className="space-y-2">
                            <DialogTitle className="text-2xl sm:text-3xl md:text-4xl font-default font-bold text-white tracking-tight">
                                Don't Miss Your <span style={{
                color: 'rgba(217, 133, 38, 1)'
              }}>Dream House</span>
                            </DialogTitle>
                            <DialogDescription className="text-primary-foreground/80 text-base sm:text-lg max-w-lg mx-auto text-center">
                                Be the first to know when new owner‑financed properties across the country match your budget.
                            </DialogDescription>
                        </div>
                    </DialogHeader>

                    <div className="relative z-10 mt-4 mb-2 max-h-[60vh] overflow-y-auto px-1">
                        <FormProvider formId="welcome-lead-capture" sectionName="Welcome Modal" defaultValues={{
            fullName: '',
            email: '',
            phone: '',
            hearAboutUs: '',
            primaryCity: '',
            primaryState: '',
            secondCity: '',
            secondState: '',
            thirdCity: '',
            thirdState: '',
            minDownPayment: '',
            maxDownPayment: ''
          }} validationRules={{
            fullName: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: 'email'
            },
            phone: {
              required: true,
              minLength: 10
            },
            hearAboutUs: {
              required: false
            },
            primaryCity: {
              required: false
            },
            primaryState: {
              required: false
            },
            secondCity: {
              required: false
            },
            secondState: {
              required: false
            },
            thirdCity: {
              required: false
            },
            thirdState: {
              required: false
            },
            minDownPayment: {
              required: false
            },
            maxDownPayment: {
              required: false
            }
          }} submitText="Get Property Alerts" successMessage="You're on the list! We'll send you matching properties soon.">
                            <LeadForm onSuccess={() => handleOpenChange(false)} />
                        </FormProvider>
                    </div>
                </DialogContent>
            </Dialog>
        </section>;
}
function LeadForm({
  onSuccess
}: {
  onSuccess: () => void;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      onSuccess();
    }
  }, [isSubmitted, submitError, onSuccess]);
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-green-500/10 border border-green-500/20 rounded-lg text-center animate-in fade-in zoom-in duration-300">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-green-500/20 text-green-400 mb-4">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Success!</h3>
                <p className="text-green-100/80">{successMessage}</p>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-5">
                <FormField control={control} name="fullName" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary-foreground/90 font-medium">Full Name</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-primary-foreground/50" />
                                    <Input placeholder="John Doe" {...field} className="pl-10 bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-ring focus-visible:border-ring h-11" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-red-400" />
                        </FormItem>} />
                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^\S+@\S+$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary-foreground/90 font-medium">Email Address</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-primary-foreground/50" />
                                    <Input type="email" placeholder="john@example.com" {...field} className="pl-10 bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-ring focus-visible:border-ring h-11" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-red-400" />
                        </FormItem>} />
                <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary-foreground/90 font-medium">Phone Number</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-primary-foreground/50" />
                                    <Input type="tel" placeholder="(555) 123-4567" {...field} className="pl-10 bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-ring focus-visible:border-ring h-11" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-red-400" />
                        </FormItem>} />
                <FormField control={control} name="hearAboutUs" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary-foreground/90 font-medium">How Did You Hear About Us?</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Info className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-primary-foreground/50" />
                                    <select {...field} className="w-full pl-10 pr-4 bg-primary-foreground/5 border border-primary-foreground/20 text-white rounded-md focus-visible:ring-ring focus-visible:border-ring h-11 appearance-none outline-none">
                                        <option value="" className="bg-primary text-white">Select One</option>
                                        <option value="Craigslist" className="bg-primary text-white">Craigslist</option>
                                        <option value="Facebook" className="bg-primary text-white">Facebook</option>
                                        <option value="Instagram" className="bg-primary text-white">Instagram</option>
                                        <option value="TikTok" className="bg-primary text-white">TikTok</option>
                                        <option value="Google Search" className="bg-primary text-white">Google Search</option>
                                        <option value="Friend or Family Referral" className="bg-primary text-white">Friend or Family Referral</option>
                                        <option value="Other" className="bg-primary text-white">Other</option>
                                    </select>
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                        <svg className="w-4 h-4 text-primary-foreground/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </FormControl>
                            <FormMessage className="text-red-400" />
                        </FormItem>} />
                <FormField control={control} name="primaryCity" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary-foreground/90 font-medium">Primary City</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-primary-foreground/50" />
                                    <Input placeholder="City" {...field} className="pl-10 bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-ring focus-visible:border-ring h-11" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-red-400" />
                        </FormItem>} />
                <FormField control={control} name="primaryState" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary-foreground/90 font-medium">Primary State</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-primary-foreground/50" />
                                    <Input placeholder="State" {...field} className="pl-10 bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-ring focus-visible:border-ring h-11" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-red-400" />
                        </FormItem>} />
                <FormField control={control} name="secondCity" render={({
        field
      }) => <FormItem>
                            <div>
                                <FormLabel className="text-primary-foreground/90 font-medium">Second City</FormLabel>
                                <p className="text-[10px] text-primary-foreground/60 leading-none mt-0.5">(Optional)</p>
                            </div>
                            <FormControl>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-primary-foreground/50" />
                                    <Input placeholder="City" {...field} className="pl-10 bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-ring focus-visible:border-ring h-11" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-red-400" />
                        </FormItem>} />
                <FormField control={control} name="secondState" render={({
        field
      }) => <FormItem>
                            <div>
                                <FormLabel className="text-primary-foreground/90 font-medium">Second State</FormLabel>
                                <p className="text-[10px] text-primary-foreground/60 leading-none mt-0.5">(Optional)</p>
                            </div>
                            <FormControl>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-primary-foreground/50" />
                                    <Input placeholder="State" {...field} className="pl-10 bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-ring focus-visible:border-ring h-11" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-red-400" />
                        </FormItem>} />
                <FormField control={control} name="thirdCity" render={({
        field
      }) => <FormItem>
                            <div>
                                <FormLabel className="text-primary-foreground/90 font-medium">Third City</FormLabel>
                                <p className="text-[10px] text-primary-foreground/60 leading-none mt-0.5">(Optional)</p>
                            </div>
                            <FormControl>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-primary-foreground/50" />
                                    <Input placeholder="City" {...field} className="pl-10 bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-ring focus-visible:border-ring h-11" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-red-400" />
                        </FormItem>} />
                <FormField control={control} name="thirdState" render={({
        field
      }) => <FormItem>
                            <div>
                                <FormLabel className="text-primary-foreground/90 font-medium">Third State</FormLabel>
                                <p className="text-[10px] text-primary-foreground/60 leading-none mt-0.5">(Optional)</p>
                            </div>
                            <FormControl>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-primary-foreground/50" />
                                    <Input placeholder="State" {...field} className="pl-10 bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-ring focus-visible:border-ring h-11" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-red-400" />
                        </FormItem>} />
                <FormField control={control} name="minDownPayment" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary-foreground/90 font-medium">Min Down Payment</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-primary-foreground/50" />
                                    <Input placeholder="$5,000" {...field} className="pl-10 bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-ring focus-visible:border-ring h-11" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-red-400" />
                        </FormItem>} />
                <FormField control={control} name="maxDownPayment" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary-foreground/90 font-medium">Max Down Payment</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-primary-foreground/50" />
                                    <Input placeholder="$100,000" {...field} className="pl-10 bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-ring focus-visible:border-ring h-11" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-red-400" />
                        </FormItem>} />
            </div>

            {submitError && <div className="p-4 bg-destructive/20 border border-destructive/50 rounded-md flex items-start gap-3">
                    <div className="mt-0.5 text-destructive-foreground">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <p className="text-destructive-foreground text-sm font-medium">{submitError}</p>
                </div>}

            <div className="pt-2">
                <Button type="submit" disabled={isSubmitting} className="w-full bg-ring text-primary hover:bg-ring/90 h-14 text-lg font-bold shadow-lg shadow-ring/20 transition-all hover:shadow-ring/40">
                    {isSubmitting ? <>
                            <Loader className="animate-spin mr-2 size-5" />
                            Processing...
                        </> : 'Get Property Alerts'}
                </Button>
                <p className="text-center text-xs text-primary-foreground/50 mt-4">
                    By submitting this form you agree to receive communications from Dueno a Dueno Homes. We respect your privacy.
                </p>
            </div>
        </Form>;
}